import { SelectField, TextField, DateField } from '@dispatch/web/ui';
import { Box, Divider, Grid, IconButton } from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  Control,
  FieldValues,
  UseFormSetValue,
  UseFormWatch,
  useFieldArray,
} from 'react-hook-form';
import {
  ResourceBulkForm,
  SelectedAttributeGroup,
} from '@dispatch/shared/types';

type MasterGroupAttribute = {
  title: string;
  value: string;
};

export type GroupAttributesBulkFormProps = {
  control: Control<FieldValues>;
  watch: UseFormWatch<ResourceBulkForm>;
  setValue: UseFormSetValue<ResourceBulkForm>;
  // Master list of group attributes for this tenant ({ id, attributeGroupName }).
  groupAttributesData?: { id: string; attributeGroupName: string }[];
  onStage?: () => void;
};

/**
 * Bulk grouped-attribute view (ADO #98800). Mirrors the single-resource group
 * picker in AttributesTab.tsx, but writes directly into the bulk form's
 * `selectedAttributeGroups` field array so the parent kind-gate can observe
 * whether any group is staged. Individual-attribute views are untouched.
 *
 * Permission gating is handled by the ancestor `<fieldset disabled>`
 * (`!hasUpsertPermissions`) in BulkUpdateResource, which natively disables every
 * control rendered here — the same mechanism the other bulk sections rely on —
 * so this view intentionally takes no `disabled` prop.
 */
export function GroupAttributesBulkForm({
  control,
  watch,
  setValue,
  groupAttributesData = [],
  onStage,
}: GroupAttributesBulkFormProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'selectedAttributeGroups',
    keyName: 'fieldId',
  });

  const masterGroupAttributes: MasterGroupAttribute[] = (
    groupAttributesData || []
  ).map((groupAttr) => ({
    title: groupAttr.attributeGroupName,
    value: groupAttr.id,
  }));

  const selectedGroups =
    (watch('selectedAttributeGroups') as SelectedAttributeGroup[]) || [];

  const getFilteredGroupAttributes = () => {
    const selectedGroupIds = selectedGroups.map((attr) => attr.id);
    return masterGroupAttributes.filter(
      (master) => !selectedGroupIds.includes(master.value)
    );
  };

  const handleGroupAttributeAdd = (value: unknown) => {
    const rawValue = value as { title: string; value: string };
    if (!rawValue?.value) {
      return;
    }
    append({
      id: rawValue.value,
      displayName: rawValue.title,
      startDate: null,
      endDate: null,
    });
    // Clear the picker selection so the same control can add the next group.
    setValue('addSelectedGroupAttributeGUID', '');
    onStage?.();
  };

  return (
    <>
      <div style={{ padding: '24px' }}>
        <Grid container rowSpacing={4} spacing={2}>
          <Grid item xs={6} data-testid="add-group-attribute">
            <SelectField
              // Remount the picker after each add so the controlled
              // Autocomplete reliably returns to its empty placeholder
              // (setValue('') alone can leave the prior selection visible).
              key={`add-group-picker-${fields.length}`}
              id="addSelectedGroupAttributeGUID"
              name="addSelectedGroupAttributeGUID"
              label="Add New Group Attribute"
              control={control}
              onValueChange={handleGroupAttributeAdd}
              options={getFilteredGroupAttributes()}
            />
          </Grid>
        </Grid>
      </div>
      <Divider />
      {fields.map((field, idx) => {
        const groupAttr = selectedGroups[idx];
        return (
          <div
            key={(field as { fieldId: string }).fieldId}
            style={{ padding: '24px' }}
            data-testid="group-attribute-row"
          >
            <Grid container spacing={4}>
              <Grid item xs={6} data-testid="group-attribute-name">
                <Box sx={{ flex: 1 }}>
                  <TextField
                    id={`selectedAttributeGroups.${idx}.displayName`}
                    name={`selectedAttributeGroups.${idx}.displayName`}
                    label="Group Attribute"
                    type="text"
                    control={control}
                    defaultValue={groupAttr?.displayName}
                    inputProps={{
                      readOnly: true,
                      endAdornment: (
                        <IconButton
                          onClick={() => remove(idx)}
                          data-testid="remove-group-attribute"
                        >
                          <Close />
                        </IconButton>
                      ),
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={3}>
                <DateField
                  id={`selectedAttributeGroups.${idx}.startDate`}
                  name={`selectedAttributeGroups.${idx}.startDate`}
                  label="Start Date"
                  control={control}
                  popperProps={{ sx: { zIndex: 10000 } }}
                />
              </Grid>
              <Grid item xs={3}>
                <DateField
                  id={`selectedAttributeGroups.${idx}.endDate`}
                  name={`selectedAttributeGroups.${idx}.endDate`}
                  label="End Date"
                  control={control}
                  popperProps={{ sx: { zIndex: 10000 } }}
                />
              </Grid>
            </Grid>
            <Divider style={{ marginTop: '16px' }} />
          </div>
        );
      })}
    </>
  );
}
